import { InAppBrowser } from '@capacitor/inappbrowser';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export const useOpenUrl = () => {
  const navigate = useNavigate();

  const customURLScheme = 'karehero://';

  /**
   * getIsCustomScheme checks if the given URL is using a custom scheme.
   */
  const getIsCustomScheme = (url: string) => {
    return url.startsWith(customURLScheme);
  };

  /**
   * formatURI formats the given URI into a URL object.
   * It handles custom schemes and ensures the URL is valid.
   */
  const formatURI = (uri: string) => {
    if (getIsCustomScheme(uri)) {
      uri = uri.replace(customURLScheme, `${window.location.origin}/`);
    }

    try {
      const url = new URL(uri);
      return url;
    } catch (error) {
      console.error('Invalid URL format:', uri, error);
      return new URL(window.location.origin);
    }
  };

  /**
   * getIsRefresh checks if the current URL should trigger a refresh.
   */
  const getIsRefresh = (uri: string) => {
    const url = formatURI(uri);
    return (
      url.pathname === '/sign-in' ||
      url.pathname === '/sign-in/native' ||
      ((url.pathname === '/authenticate' ||
        url.pathname === '/authenticate/native') &&
        !!url.searchParams.get('code') &&
        !!url.searchParams.get('state') &&
        !!url.searchParams.get('scope'))
    );
  };

  const openUrl = useCallback(
    (uri: string) => {
      try {
        console.log('opening url:', uri);
        const url = formatURI(uri);
        const path = `${url.pathname}${url.search}${url.hash}`;

        if (getIsCustomScheme(uri) && getIsRefresh(uri)) {
          console.log('is refresh with custom scheme, navigating to: ', path);
          // In certain cases, the InAppBrowser may not have registered as being
          // open before the appUrlOpen event is fired, so we need to wait.
          setTimeout(() => {
            InAppBrowser.close();
            window.location.href = path;
            return;
          }, 500);
        } else {
          console.log('navigating to: ', path, navigate.toString());
          navigate(path);
        }
      } catch (err) {
        console.error('Invalid URL received from Universal Link:', err);
      }
    },
    [navigate],
  );

  return { openUrl, getIsRefresh, getIsCustomScheme };
};
